import React from 'react';
import { Link } from 'react-router-dom';
import { buildRegistrationUrlWithRef } from '../../services/registrationsService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiExternalLink } = FiIcons;

/**
 * Displays recommended and featured events with registration buttons.
 * Registration links include the student's referring agent's referral code.
 */
export default function RecommendedEvents({
  recommendations = [],
  publicEvents = [],
  agentRefCode = null,
  loading = false,
  emptyMessage = 'No recommended events right now.'
}) {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const recommendedList = recommendations.map((r) => ({
    ...r.event,
    recommendationNote: r.recommendation_note,
    isFeatured: r.is_featured
  })).filter((e) => e && e.id);

  const recommendedIds = new Set(recommendedList.map((e) => e.id));
  const otherPublic = (publicEvents || []).filter((e) => e && !recommendedIds.has(e.id));

  const eventCard = (event, options = {}) => {
    const { isFeatured, recommendationNote } = options;
    const registerUrl = buildRegistrationUrlWithRef(event.id, agentRefCode, baseUrl);
    const imageUrl = event.featured_image_url || event.image_url;

    return (
      <div
        key={event.id}
        className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      >
        {imageUrl && (
          <div className="aspect-video bg-slate-100">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {isFeatured && (
              <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                Featured
              </span>
            )}
            <span className="text-xs text-slate-500 capitalize">{event.type}</span>
          </div>
          <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">{event.title}</h3>
          {recommendationNote && (
            <p className="text-sm text-slate-600 mb-2 line-clamp-2">{recommendationNote}</p>
          )}
          <a
            href={registerUrl}
            className="inline-flex items-center gap-2 mt-2 text-[#00AAFF] font-medium hover:underline"
          >
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            Register for this event
          </a>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 h-48 animate-pulse" />
        ))}
      </div>
    );
  }

  const hasRecommended = recommendedList.length > 0;
  const hasPublic = otherPublic.length > 0;

  if (!hasRecommended && !hasPublic) {
    return (
      <div className="text-center py-8 text-slate-500">
        <SafeIcon icon={FiCalendar} className="w-12 h-12 mx-auto mb-2 text-slate-300" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasRecommended && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Recommended for you</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedList.map((event) =>
              eventCard(event, {
                isFeatured: event.isFeatured,
                recommendationNote: event.recommendationNote
              })
            )}
          </div>
        </div>
      )}
      {hasPublic && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Other public events</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherPublic.slice(0, 6).map((event) => eventCard(event, {}))}
          </div>
        </div>
      )}
    </div>
  );
}
